"use client";

import { ArrowLeft } from "lucide-react";
import { useRouter, useSearchParams } from "next/navigation";
import { useEffect, useMemo, useState } from "react";
import { cn } from "@/lib/utils";

function safeInternalPath(value: string | null) {
  if (!value || !value.startsWith("/") || value.startsWith("//")) return null;
  if (value.startsWith("/forum")) return null;
  return value;
}

function referrerPath() {
  if (typeof document === "undefined" || typeof window === "undefined") return null;
  if (!document.referrer) return null;

  try {
    const url = new URL(document.referrer);
    if (url.origin !== window.location.origin) return null;
    return safeInternalPath(`${url.pathname}${url.search}`);
  } catch {
    return null;
  }
}

type FallbackSource = "default" | "explicit" | "referrer";

function isResultsContext(hint: string | null, href: string, source: FallbackSource) {
  if (hint === "results" || hint === "scoreboard" || hint === "league" || hint === "home") return true;
  if (source !== "default" && (href === "/" || href.startsWith("/?"))) return true;

  const firstSegment = href.split("?")[0].split("/").filter(Boolean)[0];
  return Boolean(firstSegment && !["article", "category", "tag", "search", "login", "register", "u"].includes(firstSegment));
}

export function ForumContextBackLink({ className }: { className?: string }) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const sourceHint = searchParams.get("from");
  const explicitReturnTo = safeInternalPath(searchParams.get("returnTo"));
  const [fallbackHref, setFallbackHref] = useState(explicitReturnTo ?? "/");
  const [fallbackSource, setFallbackSource] = useState<FallbackSource>(explicitReturnTo ? "explicit" : "default");

  useEffect(() => {
    const referrer = referrerPath();
    const animationFrame = window.requestAnimationFrame(() => {
      setFallbackHref(explicitReturnTo ?? referrer ?? "/");
      setFallbackSource(explicitReturnTo ? "explicit" : referrer ? "referrer" : "default");
    });

    return () => window.cancelAnimationFrame(animationFrame);
  }, [explicitReturnTo]);

  const label = useMemo(
    () => (isResultsContext(sourceHint, fallbackHref, fallbackSource) ? "Vissza az eredményekhez" : "Vissza a főoldalra"),
    [fallbackHref, fallbackSource, sourceHint],
  );

  function handleClick() {
    const state = window.history.state as { idx?: number } | null;
    const hasNextHistoryEntry = typeof state?.idx === "number" ? state.idx > 0 : window.history.length > 1;

    if (hasNextHistoryEntry) {
      router.back();
      return;
    }

    router.push(fallbackHref);
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      className={cn(
        "inline-flex w-fit items-center gap-1.5 text-xs font-bold text-[#273E47]/58 transition hover:text-[#A4243B] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#A4243B]/20",
        className,
      )}
    >
      <ArrowLeft size={14} aria-hidden="true" />
      <span>{label}</span>
    </button>
  );
}
